<script setup lang="ts">
  import { onMounted, ref, computed } from 'vue'
  import { PhoneNumbersService, type CustomerPhoneNumber as PhoneNumber } from '@/types/generated'
  import { useRoute } from 'vue-router'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import * as yup from 'yup'
  import type { GenericObject } from 'vee-validate'
  import { useTranslation } from '@/composables/useTranslation'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import {
    ClipboardIcon,
    QuestionMarkCircleIcon,
    ChevronRightIcon
  } from '@heroicons/vue/24/outline'
  import { CheckIcon } from 'lucide-vue-next'
  import InputField from '@/components/admin/form/InputField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { Button } from '@/components/ui/button'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

  const { t } = useTranslation({
    en: {
      active: 'Active',
      status: 'Status',
      statusDescription: 'Phone number availability',
      inactive: 'Inactive',

      phoneNumber: 'Phone number',
      copy: 'Copy',
      copied: 'Copied!',
      deactivationWarning:
        'Warning: Deactivating this phone number will deregister it from the phone provider. It will no longer be available for incoming calls. This action can not be undone.',
      inactiveInfo:
        'This phone number is inactive. Please contact support for more information or help.',
      description: 'Description',
      lawFirmName: 'Law Firm Name',
      address: 'Address',
      opening_hours: 'Opening Hours',
      lawyer_names: 'Lawyer Names',
      has_free_initial_consultation: 'Has free initial consultation',
      begin_message: 'Welcome message',
      forwarding_no_answer_message: 'Message for unanswered forwarded calls',
      end_message: 'Goodbye message',

      descriptionHint: 'Here you can store an internal designation for the phone number.',
      descriptionPlaceholder: 'e.g. standard AI assistant for general calls',
      lawFirmNameHint:
        'Here you specify the name of the law firm as it should be mentioned in the greeting announcement.',
      lawFirmNamePlaceholder: 'e.g. Müller & Associates Law Firm',
      originalPhoneNumberHint:
        'If you forward our generated number internally, please enter the original number here that clients call. This ensures we can correctly assign calls.',
      originalPhoneNumberPlaceholder: 'Your own law firm number, e.g. +49555555555555',
      freeConsultationHint:
        'If a free initial consultation is offered, this should be activated here. The AI can then point out that the initial consultation is free.',
      addressHint:
        'Here you can specify the address of the law firm. We will pass this on to the caller upon request. If you do not want unannounced client visits, please provide a corresponding note here.',
      addressPlaceholder: 'e.g. Musterstraße 1\n12345 Musterstadt',
      openingHoursHint:
        'Here you can enter the opening hours. These can be reproduced by the AI upon request.',
      openingHoursPlaceholder: 'e.g. Mondays to Fridays, from 9 am to 4 pm',
      lawyerNamesHint:
        "Here the names of the lawyers are entered. This helps the AI to better recognize them and distinguish them from the caller's name. Only enter the name here and no other designation such as lawyer or case worker.",
      lawyerNamesPlaceholder: 'e.g. Max Müller\nLisa Müller',
      beginMessageHint:
        'Here you can store a welcome message for the law firm. This will be played to the caller in advance before the call is answered.',
      beginMessagePlaceholder:
        "current message:\nHello, my name is Marie and I am the AI call answering service for {'{'}law firm name{'}'}. I will be happy to take your enquiry and pass it on to our team immediately so that we can help you as quickly as possible. You can speak to me in full sentences, just as you would to a human being. How can we help you?",
      forwardingNoAnswerMessageHint:
        'This message is played when a forwarded call goes unanswered.',
      forwardingNoAnswerMessagePlaceholder:
        'current message: Unfortunately, the line is currently busy. I will be happy to take your request immediately and you will receive a response as soon as possible.',
      endMessageHint:
        'Here you can store a farewell message for the law firm. This will be played to the caller in advance before the call is answered.',
      endMessagePlaceholder:
        'The current message depends on the conversation content.\ne.g. Thank you for your call. Your request will be processed as quickly as possible.',
      ai: 'Artificial Intelligence',
      phoneNumbers: 'Phone Numbers'
    },
    de: {
      active: 'Aktiv',
      status: 'Status',
      statusDescription: 'Telefonnummer Verfügbarkeit',
      inactive: 'Inaktiv',

      phoneNumber: 'Telefonnummer',
      copy: 'Kopieren',
      copied: 'Kopiert!',
      deactivationWarning:
        'Warnung: Durch das Deaktivieren dieser Telefonnummer wird sie vom Telefonanbieter abgemeldet. Sie ist nicht mehr für eingehende Anrufe verfügbar. Diese Aktion kann nicht rückgängig gemacht werden.',
      inactiveInfo:
        'Diese Telefonnummer ist inaktiv. Bitte kontaktieren Sie den Support für weitere Informationen oder Hilfe.',
      description: 'Beschreibung',
      lawFirmName: 'Kanzleiname',
      address: 'Adresse',
      opening_hours: 'Öffnungszeiten',
      lawyer_names: 'Anwälte',
      has_free_initial_consultation: 'Erstberatung',
      begin_message: 'Begrüßungsnachricht',
      forwarding_no_answer_message: 'Nachricht bei nicht beantworteter Weiterleitung',
      end_message: 'Abschiedsnachricht',

      descriptionHint: 'Hier kann eine interne Bezeichnung für die Rufnummer hinterlegt werden.',
      descriptionPlaceholder: 'z.B. Standard-KI-Assistent für generelle Anrufe',
      lawFirmNameHint:
        'Hier wird der Name der Kanzlei angegeben, wie er in der Begrüßungsansage genannt werden soll.',
      lawFirmNamePlaceholder: 'z.B. Anwaltskanzlei Müller & Partner',
      originalPhoneNumberHint:
        'Wenn Sie die von uns generierte Nummer intern weiterleiten, geben Sie hier unbedingt die Originalnummer an, welche von den Mandanten angerufen wird. So stellen Sie sicher, dass wir die Anrufe korrekt zuordnen können.',
      originalPhoneNumberPlaceholder: 'Ihre eigene Kanzleitelefonnummer, z.B. +49555555555555',
      freeConsultationHint:
        'Wenn eine kostenlose Erstberatung angeboten wird, sollte das hier aktiviert werden. Die KI kann dann darauf hinweisen, dass die Erstberatung kostenlos ist.',
      addressHint:
        'Hier kann die Adresse der Kanzlei angegeben werden. Bei Nachfrage geben wir diese an den Anrufer weiter. Wenn Sie keine unangekündigten Mandantenbesuch wünschen, dann geben Sie hier bitte einen entsprechenden Hinweis.',
      addressPlaceholder: 'z.B. Musterstraße 1\n12345 Musterstadt',
      openingHoursHint:
        'Hier können die Öffnungszeiten eingetragen werden. Diese können bei Nachfrage von der KI-Wiedergegeben werden.',
      openingHoursPlaceholder: 'z.B. Montags bis Freitags, von 9.00 Uhr bis 16.00 Uhr',
      lawyerNamesHint:
        'Hier werden die Namen der Anwälte eingetragen. Damit die KI diese besser erkennt und vom Anrufernamen unterscheiden kann. Hier nur den Namen eintragen und keine weitere Bezeichnung wie zum Beispiel Anwalt oder Sachbearbeiter.',
      lawyerNamesPlaceholder: 'z.B. Max Müller\nLisa Müller',
      beginMessageHint:
        'Hier kann eine Begrüßungsnachricht für die Kanzlei hinterlegt werden. Diese wird vorab an den Anrufer gespielt, bevor der Anruf angenommen wird.',
      beginMessagePlaceholder:
        "aktuelle Nachricht: Hallo, mein Name ist Marie und ich bin die KI Anrufannahme der {'{'}Kanzleiname{'}'}. Ich nehme gerne Ihr Anliegen auf und gebe es sofort an unser Team weiter, damit wir Ihnen schnellstmöglich weiterhelfen können. Sie können mit mir in ganzen Sätzen wie mit einem Menschen sprechen. Wobei können wir behilflich sein?",
      forwardingNoAnswerMessageHint:
        'Diese Nachricht wird abgespielt, wenn eine weitergeleitete Nummer nicht abnimmt.',
      forwardingNoAnswerMessagePlaceholder:
        'aktuelle Nachricht: Leider ist die Leitung aktuell belegt. Ich nehme gerne Ihr Anliegen sofort auf und Sie erhalten schnellstmöglich eine Rückmeldung.',
      endMessageHint:
        'Hier kann eine Abschiedsnachricht für die Kanzlei hinterlegt werden. Diese wird am Schluss des Anrufes abgespielt, nachdem alle Informationen gesammelt wurden.',
      endMessagePlaceholder:
        'Die aktuelle Nachricht hängt vom Gespräch ab.\nz.B. Vielen Dank für Ihren Anruf, Ihr Anliegen wird schnellstmöglich bearbeitet.',
      ai: 'Künstliche Intelligenz',
      phoneNumbers: 'Telefonnummern'
    },
    fr: {
      active: 'Actif',
      status: 'Statut',
      statusDescription: 'Disponibilité du numéro de téléphone',
      inactive: 'Inactif',

      phoneNumber: 'Numéro de téléphone',
      copy: 'Copier',
      copied: 'Copié !',
      deactivationWarning:
        'Attention : La désactivation de ce numéro de téléphone entraînera sa désinscription auprès du fournisseur téléphonique. Il ne sera plus disponible pour les appels entrants. Cette action est irréversible.',
      inactiveInfo:
        "Ce numéro de téléphone est inactif. Veuillez contacter le support pour plus d'informations ou de l'aide.",
      description: 'Description',
      lawFirmName: "Nom du cabinet d'avocats",
      address: 'Adresse',
      opening_hours: "Horaires d'ouverture",
      lawyer_names: 'Noms des avocats',
      has_free_initial_consultation: 'Consultation initiale gratuite',
      begin_message: "Message d'accueil",
      forwarding_no_answer_message: 'Message pour les appels transférés sans réponse',
      end_message: "Message d'au revoir",

      descriptionHint:
        'Vous pouvez saisir ici une désignation interne pour le numéro de téléphone.',
      descriptionPlaceholder: 'p. ex. Assistant IA standard pour les appels généraux',
      lawFirmNameHint:
        "Indiquez ici le nom du cabinet d'avocats tel qu'il doit être mentionné dans l'annonce d'accueil.",
      lawFirmNamePlaceholder: "p. ex. Cabinet d'avocats Müller & Associés",
      originalPhoneNumberHint:
        "Si vous transférez notre numéro généré en interne, veuillez saisir ici le numéro d'origine que les clients appellent. Cela nous permet d'attribuer correctement les appels.",
      originalPhoneNumberPlaceholder: 'Votre propre numéro de cabinet, p. ex. +33555555555555',
      freeConsultationHint:
        "Si une consultation initiale gratuite est proposée, elle doit être activée ici. L'IA peut alors indiquer que la consultation initiale est gratuite.",
      addressHint:
        "Vous pouvez saisir ici l'adresse du cabinet d'avocats. Nous la transmettrons à l'appelant sur demande. Si vous ne souhaitez pas de visites de clients sans rendez-vous, veuillez fournir une note correspondante ici.",
      addressPlaceholder: 'p. ex. 1 rue de la Paix\n75001 Paris',
      openingHoursHint:
        "Vous pouvez saisir ici les horaires d'ouverture. L'IA peut les communiquer sur demande.",
      openingHoursPlaceholder: 'p. ex. Du lundi au vendredi, de 9h00 à 16h00',
      lawyerNamesHint:
        "Saisissez ici les noms des avocats. Cela aide l'IA à mieux les reconnaître et à les distinguer du nom de l'appelant. Saisissez uniquement le nom, sans autre désignation comme avocat ou collaborateur.",
      lawyerNamesPlaceholder: 'p. ex. Max Müller\nLisa Müller',
      beginMessageHint:
        "Vous pouvez saisir ici un message d'accueil pour le cabinet. Celui-ci sera diffusé à l'appelant avant que l'appel ne soit pris.",
      beginMessagePlaceholder:
        "message actuel :\nBonjour, je m'appelle Marie et je suis le répondeur IA du {'{'}nom du cabinet{'}'}. Je me ferai un plaisir de prendre votre demande et de la transmettre immédiatement à notre équipe afin que nous puissions vous aider le plus rapidement possible. Vous pouvez me parler en phrases complètes, comme à un être humain. Comment pouvons-nous vous aider ?",
      forwardingNoAnswerMessageHint:
        "Ce message est diffusé lorsqu'un appel transféré reste sans réponse.",
      forwardingNoAnswerMessagePlaceholder:
        'message actuel : Malheureusement, la ligne est actuellement occupée. Je me ferai un plaisir de prendre votre demande immédiatement et vous recevrez une réponse dans les plus brefs délais.',
      endMessageHint:
        "Vous pouvez saisir ici un message d'au revoir pour le cabinet. Celui-ci sera diffusé à la fin de l'appel, après que toutes les informations ont été recueillies.",
      endMessagePlaceholder:
        'Le message actuel dépend du contenu de la conversation.\np. ex. Merci pour votre appel. Votre demande sera traitée dans les plus brefs délais.',
      ai: 'Intelligence artificielle',
      phoneNumbers: 'Numéros de téléphone'
    }
  })

  const route = useRoute()
  const phoneNumber = ref<PhoneNumber | null>(null)
  const defaultBeginMessage = computed(() => {
    if (!phoneNumber.value?.lawfirm_name) return ''
    return `Hallo, mein Name ist Marie und ich bin die KI Anrufannahme der ${phoneNumber.value.lawfirm_name}. Ich nehme gerne Ihr Anliegen auf und gebe es sofort an unser Team weiter, damit wir Ihnen schnellstmöglich weiterhelfen können.
Sie können mit mir in ganzen Sätzen wie mit einem Menschen sprechen.
Wobei können wir behilflich sein ?
    `
  })

  const defaultForwardingNoAnswerMessage =
    'Leider ist die Leitung aktuell belegt. Ich nehme aber gerne Ihr Anliegen sofort auf und Sie erhalten schnellstmöglich eine Rückmeldung.'

  onMounted(async () => {
    try {
      phoneNumber.value = (await PhoneNumbersService.phoneNumbersRetrieve({
        uuid: route.params.uuid.toString()
      })) as PhoneNumber
    } catch (error) {
      console.error('Error fetching phone number:', error)
    }
  })

  const handleSave = async (values: GenericObject) => {
    try {
      // Remove phone_number from values since it's read-only and shouldn't be updated
      const { phone_number, ...updateData } = values

      await PhoneNumbersService.phoneNumbersPartialUpdate({
        uuid: phoneNumber.value!.uuid!,
        requestBody: updateData
      })
    } catch (error) {
      console.error('Error updating phone number:', error)
    }
  }

  const copySuccess = ref(false)

  const copyPhoneNumberToClipboard = () => {
    if (phoneNumber.value?.phone_number) {
      navigator.clipboard.writeText(phoneNumber.value.phone_number)
      copySuccess.value = true
      setTimeout(() => {
        copySuccess.value = false
      }, 2000)
    }
  }
</script>

<template>
  <TooltipProvider>
    <SettingsPage>
      <template #header>
        <div class="flex items-center justify-between">
          <nav class="flex items-center gap-2 text-sm text-gray-500">
            <div>{{ t('ai') }}</div>
            <ChevronRightIcon class="size-3" />
            <div>{{ t('phoneNumbers') }}</div>
            <ChevronRightIcon class="size-3" />
            <span class="font-medium text-gray-900">{{ phoneNumber?.phone_number }}</span>
          </nav>
        </div>
      </template>

      <div
        v-if="phoneNumber"
        class="max-w-4xl space-y-6"
      >
        <!-- Inactive Status Warning -->
        <div
          v-if="!phoneNumber.active"
          class="rounded-lg border border-red-200 bg-red-50 p-4"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <svg
                class="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-red-700">
                {{ t('inactiveInfo') }}
              </p>
            </div>
          </div>
        </div>

        <AutoSavingForm
          class="flex flex-col gap-3"
          @save="handleSave"
        >
          <!-- Active Status Section -->
          <div class="mb-6">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-sm font-medium text-gray-900">{{ t('status') }}</h3>
                <p class="text-xs text-gray-500">{{ t('statusDescription') }}</p>
              </div>
              <div class="flex items-center gap-3">
                <div class="flex items-center gap-2">
                  <div
                    :class="[
                      'h-3 w-3 rounded-full',
                      phoneNumber.active ? 'bg-green-500' : 'bg-red-500'
                    ]"
                  ></div>
                  <span class="text-sm font-medium text-gray-900">
                    {{ phoneNumber.active ? t('active') : t('inactive') }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Phone Number Section -->
          <div class="mb-6">
            <div class="mb-2">
              <h3 class="text-sm font-medium text-gray-900">{{ t('phoneNumber') }}</h3>
            </div>
            <div class="relative">
              <InputField
                :model-value="phoneNumber.phone_number"
                name="phone_number"
                :rules="yup.string()"
                disabled
              />
              <Button
                v-if="!copySuccess"
                type="button"
                variant="ghost"
                size="sm"
                class="absolute right-3 top-1/2 h-8 w-8 -translate-y-1/2 p-0"
                @click="copyPhoneNumberToClipboard"
              >
                <ClipboardIcon class="size-4" />
              </Button>
              <!-- Copy success indicator -->
              <div
                v-if="copySuccess"
                class="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1 rounded bg-green-100 px-2 py-1
                  text-xs text-green-700"
              >
                <CheckIcon class="h-3 w-3" />
                {{ t('copied') }}
              </div>
            </div>
          </div>

          <!-- Form Fields -->
          <div class="space-y-4">
            <!-- Description -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">{{ t('description') }}</label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('descriptionHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <InputField
                v-model="phoneNumber.description"
                name="description"
                :rules="yup.string()"
                :placeholder="t('descriptionPlaceholder')"
              />
            </div>

            <!-- Law Firm Name -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">{{ t('lawFirmName') }}</label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('lawFirmNameHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <InputField
                v-model="phoneNumber.lawfirm_name"
                name="lawfirm_name"
                :rules="yup.string()"
                :placeholder="t('lawFirmNamePlaceholder')"
              />
            </div>

            <!-- Original Phone Number -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">
                  Original {{ t('phoneNumber') }}
                </label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('originalPhoneNumberHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <InputField
                v-model="phoneNumber.original_phone_number"
                name="original_phone_number"
                :rules="yup.string()"
                :placeholder="t('originalPhoneNumberPlaceholder')"
              />
            </div>

            <!-- Free Consultation Checkbox -->
            <div class="rounded-lg bg-gray-50 p-3">
              <div class="flex items-start gap-3">
                <CheckboxField
                  v-model="phoneNumber.has_free_initial_consultation"
                  :label="t('has_free_initial_consultation')"
                  name="has_free_initial_consultation"
                />
                <div class="flex-1">
                  <p class="text-sm text-gray-600">{{ t('freeConsultationHint') }}</p>
                </div>
              </div>
            </div>

            <!-- Address -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">{{ t('address') }}</label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('addressHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <TextAreaField
                v-model="phoneNumber.address"
                name="address"
                :rules="yup.string()"
                :placeholder="t('addressPlaceholder')"
              />
            </div>

            <!-- Opening Hours -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">{{ t('opening_hours') }}</label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('openingHoursHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <TextAreaField
                v-model="phoneNumber.opening_hours"
                name="opening_hours"
                :rules="yup.string()"
                :placeholder="t('openingHoursPlaceholder')"
              />
            </div>

            <!-- Lawyer Names -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">{{ t('lawyer_names') }}</label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('lawyerNamesHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <TextAreaField
                v-model="phoneNumber.lawyer_names"
                name="lawyer_names"
                :rules="yup.string()"
                :placeholder="t('lawyerNamesPlaceholder')"
              />
            </div>

            <!-- Welcome Message -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">{{ t('begin_message') }}</label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('beginMessageHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <TextAreaField
                v-model="phoneNumber.begin_message"
                :value="phoneNumber.begin_message ?? defaultBeginMessage"
                name="begin_message"
                :rules="yup.string()"
                :placeholder="t('beginMessagePlaceholder')"
                rows="5"
              />
            </div>

            <!-- Message for Unanswered Forwarded Calls -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">
                  {{ t('forwarding_no_answer_message') }}
                </label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('forwardingNoAnswerMessageHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <TextAreaField
                v-model="phoneNumber.forwarding_no_answer_message"
                :value="phoneNumber.forwarding_no_answer_message ?? ''"
                name="forwarding_no_answer_message"
                :rules="yup.string()"
                :placeholder="t('forwardingNoAnswerMessagePlaceholder')"
                rows="5"
              />
            </div>

            <!-- Goodbye Message -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">{{ t('end_message') }}</label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('endMessageHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <TextAreaField
                v-model="phoneNumber.end_message"
                :value="phoneNumber.end_message ?? ''"
                name="end_message"
                :rules="yup.string()"
                :placeholder="t('endMessagePlaceholder')"
                rows="5"
              />
            </div>
          </div>
        </AutoSavingForm>
      </div>
    </SettingsPage>
  </TooltipProvider>
</template>
